import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import KpiStatCard from "../components/KpiStatCard";
import AddItemModal from "../../SupplyChain/ItemSettings/AddItemModal";
import ItemPreviewDrawer from "../../SupplyChain/ItemSettings/ItemPreviewDrawer";
import { decorateItem } from "../../SupplyChain/ItemSettings/ItemMasterService";
import { toast } from "react-toastify";
import { safeText } from "../utils/hubHelpers";

export default function ItemMasterHub() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStockType, setSelectedStockType] = useState("");
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [editItemData, setEditItemData] = useState(null);
  const [modalState, setModalState] = useState({ EditItemData: null });
  const [previewItem, setPreviewItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    fetchItems();
  }, [selectedStockType]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let query = `pageSize=500`;
      if (selectedStockType !== "" && selectedStockType !== undefined) {
        query += `&stockType=${selectedStockType}`;
      }
      if (searchQuery) query += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await callApi(`${apiUrl.get_items}${query}`, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.item)
          ? res.data.item
          : Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data?.rows)
              ? res.data.rows
              : Array.isArray(res?.data)
                ? res.data
                : [];
        setItems(list);
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const safeItems = Array.isArray(items) ? items : [];

  const filteredItems = safeItems.filter((it) => {
    if (!it) return false;
    if (selectedStockType === "1") {
      if (it.stockType !== 1 && it.stockType !== "1") return false;
    } else if (selectedStockType === "0") {
      if (it.stockType === 1 || it.stockType === "1") return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (it.name || "").toLowerCase();
      const hsn = (it.hsnCode || "").toLowerCase();
      const unit = (it.unit || "").toLowerCase();
      const head = (it.expensehead?.name || it.expenseHead?.name || "").toLowerCase();
      const code = (it.itemCode || "").toLowerCase();
      if (!name.includes(q) && !hsn.includes(q) && !unit.includes(q) && !head.includes(q) && !code.includes(q)) {
        return false;
      }
    }
    return true;
  });

  const totalItems = safeItems.length;
  const consumableItems = safeItems.filter((i) => i.stockType === 1 || i.stockType === "1").length;
  const fixedAssetItems = safeItems.filter((i) => i.stockType !== 1 && i.stockType !== "1").length;
  const itemsWithHsn = safeItems.filter((i) => i.hsnCode && i.hsnCode.length > 0).length;

  // Pagination calculation
  const totalPages = Math.ceil(filteredItems.length / pageSize) || 1;
  const paginatedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleAddNew = () => {
    setEditItemData(null);
    setIsAddItemOpen(true);
  };

  const handleEdit = (item) => {
    setEditItemData(item);
    setIsAddItemOpen(true);
  };

  const handlePreview = (item) => {
    const decorated = decorateItem(item);
    setPreviewItem({
      ...decorated,
      category: item?.expensehead?.expenseheadtype?.name?.toLowerCase() || item?.category || "supply",
      expenseHead: item?.expensehead?.name || item?.expenseHead?.name || "--",
      itemType: item?.stockType === 1 ? "Consumable Asset" : "Fixed Asset",
      rateDisplay: item?.standardRate ? `₹ ${item.standardRate}` : "--",
      gstDisplay: item?.gstRate ? `${item.gstRate}%` : "--",
    });
  };

  const handleStatusToggle = (data, status) => {
    const postData = {
      status: !status,
    };

    const options = {
      method: "POST",
      data: postData,
    };
    let API = apiUrl?.update_items + `?itemId=${data?.id}`;
    callApi(API, options).then((response) => {
      if (response?.status === true) {
        toast.success(response?.message || "Item status updated");
        fetchItems();
        if (previewItem?.id === data?.id) {
          setPreviewItem((prev) => ({
            ...prev,
            status: !status,
          }));
        }
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    });
  };

  return (
    <div className="item-master-hub-view">
      {/* 1. KPI Stats Grid */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Total Master Catalog"
          value={totalItems}
          subtext="Configured materials & parts"
          icon={<span>🏷️</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Consumables & Spares"
          value={consumableItems}
          subtext="Oils, filters, hardware & parts"
          icon={<span>🛢️</span>}
          color="#10b981"
          iconBg="#ecfdf5"
        />

        <KpiStatCard
          label="Machinery & Fixed Assets"
          value={fixedAssetItems}
          subtext="Heavy machinery & asset items"
          icon={<span>🚜</span>}
          color="#8b5cf6"
          iconBg="#f5f3ff"
        />

        <KpiStatCard
          label="HSN / SAC Compliant"
          value={itemsWithHsn}
          subtext="GST ready tax codes"
          icon={<span>📑</span>}
          color="#059669"
          iconBg="#f0fdf4"
        />
      </div>

      {/* 2. Filter & Quick Category Tabs Toolbar */}
      <div className="hub-toolbar">
        <div className="toolbar-left d-flex align-items-center gap-2 flex-wrap">
          {/* Quick Category Tabs */}
          <div className="d-flex align-items-center gap-1 bg-light p-1 rounded-2 border" style={{ height: 34, boxSizing: "border-box" }}>
            <button
              type="button"
              className={`btn btn-sm ${selectedStockType === "" ? "btn-dark shadow-sm" : "btn-light text-secondary border-0"}`}
              onClick={() => {
                setSelectedStockType("");
                setCurrentPage(1);
              }}
              style={{ fontSize: "12px", fontWeight: 600, borderRadius: 4, height: 26, padding: "0 10px", display: "inline-flex", alignItems: "center" }}
            >
              All Items ({totalItems})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${selectedStockType === "1" ? "btn-success shadow-sm" : "btn-light text-secondary border-0"}`}
              onClick={() => {
                setSelectedStockType("1");
                setCurrentPage(1);
              }}
              style={{ fontSize: "12px", fontWeight: 600, borderRadius: 4, height: 26, padding: "0 10px", display: "inline-flex", alignItems: "center" }}
            >
              📦 Consumables ({consumableItems})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${selectedStockType === "0" ? "btn-primary shadow-sm" : "btn-light text-secondary border-0"}`}
              onClick={() => {
                setSelectedStockType("0");
                setCurrentPage(1);
              }}
              style={{ fontSize: "12px", fontWeight: 600, borderRadius: 4, height: 26, padding: "0 10px", display: "inline-flex", alignItems: "center" }}
            >
              🚜 Fixed Assets ({fixedAssetItems})
            </button>
          </div>

          <div className="search-box-wrap" style={{ minWidth: 260 }}>
            <span className="search-icon-pos">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search part name, HSN, expense head..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="toolbar-right">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddNew}
            style={{
              background: "#059669",
              borderColor: "#059669",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              boxShadow: "0 2px 6px rgba(5, 150, 105, 0.25)",
            }}
          >
            <span>+</span> Add New Master Item
          </button>
        </div>
      </div>

      {/* 3. Items Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status" style={{ marginBottom: "1rem" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Loading Item Master Catalog...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: "4rem 2rem",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem" }}>🏷️</span>
          <h4 style={{ color: "#0f172a", fontWeight: 700 }}>No Master Items Found</h4>
          <p style={{ color: "#64748b", maxWidth: 420, margin: "0 auto 1.5rem auto", fontSize: "0.875rem" }}>
            No items matched your search query. Register spare parts, consumables, or machinery tools.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddNew}
          >
            + Add Master Item
          </button>
        </div>
      ) : (
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-white">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.9rem" }}>
              <thead style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", color: "#475569" }}>
                <tr>
                  <th style={{ width: "34%", padding: "1rem 1.25rem", textAlign: "left" }}>Item Name & Specifications</th>
                  <th style={{ width: "20%", padding: "1rem 1rem", textAlign: "left" }}>Expense Head</th>
                  <th style={{ width: "14%", padding: "1rem 1rem", textAlign: "left" }}>HSN / SAC Code</th>
                  <th style={{ width: "10%", padding: "1rem 1rem", textAlign: "left" }}>Standard Unit</th>
                  <th style={{ width: "14%", padding: "1rem 1rem", textAlign: "left" }}>Stock Classification</th>
                  <th style={{ width: "8%", padding: "1rem 1.25rem", textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => {
                  const isConsumable = item.stockType === 1 || item.stockType === "1";
                  const isTools = item.stockType === 2 || item.stockType === "2";
                  const isAssets = item.stockType === 0 || item.stockType === "0";
                  return (
                    <tr
                      key={item.id}
                      style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                      onClick={() => handlePreview(item)}
                    >
                      <td style={{ padding: "0.85rem 1.25rem", textAlign: "left" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: "16px" }}>{isConsumable ? "📦" : isTools ? "🛠️" : isAssets ? "🚜" : ""}</span>
                          <div>
                            <div className="fw-bold text-dark hover-primary" style={{ fontSize: "13.5px" }}>
                              {safeText(item.name, "Item")}
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-0.5">
                              {item.itemCode && (
                                <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: "11.5px" }}>
                                  #{item.itemCode}
                                </span>
                              )}
                              {item.description && (
                                <span className="text-muted text-truncate" style={{ fontSize: "12px", maxWidth: 300 }}>
                                  {safeText(item.description)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={{ padding: "0.85rem 1rem", color: "#334155", fontWeight: 600, fontSize: "13px", textAlign: "left" }}>
                        📁 {safeText(item.expensehead?.name || item.expenseHead?.name, "General Head")}
                      </td>

                      <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace", color: "#2563eb", fontWeight: 700, fontSize: "13px", textAlign: "left" }}>
                        {safeText(item.hsnCode, "—")}
                      </td>

                      <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                        <span style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: "12px", color: "#334155" }}>
                          {safeText(item.unit, "Nos")}
                        </span>
                      </td>

                      <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                        <span
                          style={{
                            padding: "3px 9px",
                            borderRadius: 20,
                            fontSize: "12px",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            color: isConsumable ? "#059669" : isTools ? "#b45309" : isAssets ? "#7c3aed" : "#64748b",
                            background: isConsumable ? "#dcfce7" : isTools ? "#fef3c7" : isAssets ? "#f5f3ff" : "#f8fafc",
                            border: `1px solid ${isConsumable ? "#bbf7d0" : isTools ? "#fde68a" : isAssets ? "#ddd6fe" : "#e2e8f0"}`,
                          }}
                        >
                          <span>{isConsumable ? "●" : isTools ? "●" : isAssets ? "●" : ""}</span>
                          <span>{isConsumable ? "Consumable Spare" : isTools ? "Tools" : "Fixed Asset"}</span>
                        </span>
                      </td>

                      <td
                        style={{ padding: "0.85rem 1.25rem", textAlign: "right" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <button
                            type="button"
                            onClick={() => handlePreview(item)}
                            className="btn btn-sm btn-light border"
                            title="Inspect Site Stock"
                            style={{
                              padding: "5px 10px",
                              fontSize: "13px",
                              fontWeight: 600,
                              borderRadius: 6,
                            }}
                          >
                            👁️
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              padding: "5px 10px",
                              fontSize: "13px",
                              fontWeight: 600,
                              borderRadius: 6,
                              background: "#fff",
                            }}
                          >
                            ✏️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div
              className="p-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}
            >
              <span className="text-muted small" style={{ fontSize: "12.5px" }}>
                Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to{" "}
                <strong>{Math.min(currentPage * pageSize, filteredItems.length)}</strong> of{" "}
                <strong>{filteredItems.length}</strong> items
              </span>

              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{ fontSize: "13px", padding: "5px 12px", borderRadius: 6, fontWeight: 600 }}
                >
                  ◀ Prev
                </button>

                <span className="px-2 small text-dark fw-bold" style={{ fontSize: "13px" }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{ fontSize: "13px", padding: "5px 12px", borderRadius: 6, fontWeight: 600 }}
                >
                  Next ▶
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add / Edit Item Modal */}
      {isAddItemOpen && (
        <AddItemModal
          isOpen={isAddItemOpen}
          onClose={() => setIsAddItemOpen(false)}
          EditItemData={editItemData}
          setState={setModalState}
          getItems={fetchItems}
          itemList={items}
        />
      )}

      {/* Item Preview Drawer (Shows Site Stock & Details) */}
      <ItemPreviewDrawer
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onEdit={(item) => {
          setPreviewItem(null);
          handleEdit(item);
        }}
        onToggleStatus={handleStatusToggle}
      />
    </div>
  );
}
